// =======
// Helper Functions
// =======
window.pixeter = window.pixeter || {};

window.pixeter.getBlockedDates = async () => {
  const STORAGE_KEY = 'pixeter_blocked_dates';
  const UPDATED_KEY = 'pixeter_blocked_dates_updated';

  const cachedDates = localStorage.getItem(STORAGE_KEY);
  const lastUpdated = localStorage.getItem(UPDATED_KEY) || 0;

  // If we have cached dates and they are less than a day old, use them
  if (false && cachedDates && lastUpdated && Date.now() - parseInt(lastUpdated, 10) < 86400000) {
    window.pixeter.blockedDates = JSON.parse(cachedDates);
  } else {
    const reponse = await fetch('https://api.zapiet.com/v1.0/pickup/locations/284971/calendar?shop=7ae7e0-12.myshopify.com', {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
    });

    const data = await reponse.json();
    const disabledDates = data.disabled?.filter(Array.isArray)?.map(([y,m,d]) => `${m+1}/${d}/${y}`) || [];

    localStorage.setItem(STORAGE_KEY, JSON.stringify(disabledDates));
    localStorage.setItem(UPDATED_KEY, Date.now());
    window.pixeter.blockedDates = disabledDates;
  }

  document.dispatchEvent(new CustomEvent('pixeter:blocked-dates-updated'));
};

window.pixeter.getBlockedDates();

// =======
// Custom Delivery Date Selector
// =======
class CustomDelivery extends HTMLElement {
  static states = {
    CLOSED: 'closed',
    OPEN: 'open',
    ACTIVE: 'active',
  };

  static option = {
    PICKUP: 'Pickup',
    DELIVERY: 'Delivery',
  };

  constructor() {
    super();
    
    // Get elements
    this.dateSelector = this.querySelector('[data-selector]');
    this.dateField = this.querySelector('[data-delivery-date]');
    this.activeInfo = this.querySelector('[data-active-info]');
    this.activeDate = this.querySelector('[data-active-date]');
    this.closeBtn = this.querySelector('[data-close]');
    this.toggleWrap = this.querySelector('[data-toggle-wrap]');
    this.toggleDeliveryBtn = this.querySelector('[data-toggle-delivery]');
    this.togglePickupBtn = this.querySelector('[data-toggle-pickup]');
    this.removeBtn = this.querySelector('[data-remove]');
    this.saveBtn = this.querySelector('[data-save]');

    // Get attributes
    this.deliveryDate = this.dataset.date;
    this.itemKey = this.dataset.itemKey;
    this.cartLine = this.dataset.cartLine;

    // Set initial state
    this.state = this.deliveryDate === '' ? CustomDelivery.states.CLOSED : CustomDelivery.states.ACTIVE;
    this.option = CustomDelivery.option.DELIVERY;

    // Bind methods
    this.toggleDeliveryBtn.addEventListener('click', this.handleShowDelivery.bind(this));
    this.togglePickupBtn.addEventListener('click', this.handleShowPickup.bind(this));

    this.closeBtn.addEventListener('click', this.handleClose.bind(this));
    this.removeBtn.addEventListener('click', this.handleRemove.bind(this));
    this.saveBtn.addEventListener('click', this.handleSave.bind(this));
    document.addEventListener('pixeter:blocked-dates-updated', this.updateDatepicker.bind(this));

    // Setup date picker
    this.datepickerOptions = {
      minDate: new Date(Date.now() + 86400000), // Add 24 hours in milliseconds
      autohide: true,
      datesDisabled: window.pixeter.blockedDates || [],
      daysOfWeekDisabled: [0, 6],
    };
    
    this.datepicker = new Datepicker(this.dateField, this.datepickerOptions);
  }

  updateDatepicker() {
    this.datepicker.setOptions({
      ...this.datepickerOptions,
      datesDisabled: window.pixeter.blockedDates || [],
    });
  }

  updateState(state) {
    this.state = state ;
    this.dateSelector.classList.toggle('hide', this.state !== CustomDelivery.states.OPEN);
    this.toggleWrap.classList.toggle('hide', this.state !== CustomDelivery.states.CLOSED);
    this.activeInfo.classList.toggle('hide', this.state !== CustomDelivery.states.ACTIVE);
  }

  handleShowPickup(e) {
    e.preventDefault();
    this.option = CustomDelivery.option.PICKUP;
    this.updateState(CustomDelivery.states.OPEN);
  }

  handleShowDelivery(e) {
    e.preventDefault();
    this.option = CustomDelivery.option.DELIVERY;
    this.updateState(CustomDelivery.states.OPEN);
  }
  
  handleClose(e) {
    e.preventDefault();
    this.updateState(CustomDelivery.states.CLOSED);
  }

  fetchCurrentProperties() {

  }

  async updateItem() {
    const cartJson = await fetch('/cart.js');
    const cart = await cartJson.json();
    const item = cart.items[this.cartLine - 1];

    if (!item) {
      throw new Error('Cart item not found');
    }

    const changeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        line: this.cartLine,
        properties: {
          ...item.properties,
          'Custom Date': this.deliveryDate,
          'Custom Delivery': this.option,
        },
      }),
    });

    if (changeResponse.status !== 200) {
      throw new Error('Failed to update cart item');
    }

    return true;
  }

  async handleRemove(e) {
    e.preventDefault();
    this.deliveryDate = '';

    this.classList.add('loading');

    try {
      await this.updateItem();
      this.updateState(CustomDelivery.states.CLOSED);
    } catch (error) {
      alert('Failed to remove delivery date. Please try again.');
      throw error;
    } finally {
      this.classList.remove('loading');
    }
  }

  async handleSave(e) {
    e.preventDefault();
    this.deliveryDate = this.dateField.value;
    this.activeDate.innerHTML = `${this.option} Date: ${this.deliveryDate}`;

    this.classList.add('loading');

    try {
      await this.updateItem();
      this.updateState(CustomDelivery.states.ACTIVE);
    } catch (error) {
      alert('Failed to update delivery date. Please try again.');
      throw error;
    } finally {
      this.classList.remove('loading');
    }
  }
}

customElements.define('custom-delivery', CustomDelivery);


// =======
// The Celebration UI
// =======
class Celebration extends HTMLElement {
  constructor() {
    super();

    // Get elements
    this.atcBtn = this.querySelector('[data-atc]');
    this.price = this.querySelector('[data-price-total]');
    this.info = this.querySelector('[data-info]');

    // Get attributes
    this.pricePerEvent = parseFloat(this.dataset.pricePerEvent);

    // Set initial state
    this.enabled = false;
    this.selectedCount = 0;

    // Bind methods
    this.atcBtn.addEventListener('click', this.handleAddToCart.bind(this));
    document.addEventListener('celebration:updated', this.handleUpdate.bind(this));
  }

  updateState(selected) {
    this.count = selected.length;
    this.enabled = this.count >= 2;

    if (this.enabled) {
      this.atcBtn.removeAttribute('disabled');
      const price = selected.reduce((acc, event) => acc + event.getPrice(), 0);
      console.log(price);
      console.log(selected);
      this.price.innerHTML = `$${(price).toFixed(2).replace(/\.00$/, '')}`;
    } else {
      this.atcBtn.setAttribute('disabled', true);
    }

    this.info.innerHTML = this.enabled ? `${this.count} events selected` : 'Select at least two events';
  }

  handleUpdate() {
    const events = this.querySelectorAll('celebration-item');
    const selected = Array.from(events).filter(event => event.selected);
    this.updateState(selected);
  };

  async handleAddToCart(e) {
    e.preventDefault();

    const events = this.querySelectorAll('celebration-item');
    console.log(events);
    const selected = Array.from(events).filter(event => event.selected);
    const data = selected.map(event => event.getItem());

    this.atcBtn.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: data,
        }),
      });

      if (response.status !== 200) {
        throw new Error('Failed to add items to cart');
      }

      window.location.href = '/cart';
    } catch (error) {
      alert('Failed to add items to cart. Please try again.');
      this.atcBtn.classList.remove('loading');
      throw error;
    }
  }
}

customElements.define('celebration-selection', Celebration);

class CelebrationItem extends HTMLElement {
  constructor() {
    super();

    // Get elements
    this.cloneBtn = this.querySelector('[data-clone]');
    this.addBtn = this.querySelector('[data-add]');
    this.removeBtn = this.querySelector('[data-remove]');
    this.addedLabel = this.querySelector('[data-added]');
    this.dateField = this.querySelector('[data-date-field]');
    this.messageField = this.querySelector('[data-message-field]');
    this.addressField = this.querySelector('[data-address-field]');
    this.sizeOptions = this.querySelectorAll('[data-size-field]');
    this.zipField = this.querySelector('[data-zip-field]');
    this.cityField = this.querySelector('[data-city-field]');
    this.customization = this.querySelector('[data-customization]');
    this.messageCount = this.querySelector('[data-message-count]');
    this.messageSuggesion = this.querySelector('[data-message-suggestion]');
    this.messageSuggesionOptions = JSON.parse(this.messageSuggesion?.dataset.messages || '[]');

    // Get attributes
    this.variantId = this.dataset.variantId;
    this.occasion = this.dataset.occasion;
    this.basePrice = parseFloat(this.dataset.basePrice);

    // Set initial state
    this.selected = false;
    this.messageSuggesionIdx = 0;

    // Bind methods
    this.addBtn.addEventListener('click', this.handleAdd.bind(this));
    this.removeBtn.addEventListener('click', this.handleRemove.bind(this));
    this.messageField.addEventListener('input', this.handleMessage.bind(this));
    this.messageSuggesion?.addEventListener('click', this.handleMassageSuggestion.bind(this));
    this.cloneBtn.addEventListener('click', this.handleClone.bind(this));
    this.sizeOptions.forEach(option => option.addEventListener('change', this.handleSizeChange.bind(this)));
    document.addEventListener('pixeter:blocked-dates-updated', this.updateDatepicker.bind(this));

    // Setup date picker
    this.datepickerOptions = {
      minDate: new Date(Date.now() + 86400000), // Add 24 hours in milliseconds
      autohide: true,
      datesDisabled: window.pixeter.blockedDates || [],
      daysOfWeekDisabled: [0, 6],
    };

    this.datepicker = new Datepicker(this.dateField, this.datepickerOptions);
  }

  updateDatepicker() {
    this.datepicker.setOptions({
      ...this.datepickerOptions,
      datesDisabled: window.pixeter.blockedDates || [],
    });
  }

  getPrice() {
    return parseFloat([...this.sizeOptions].find(option => option.checked)?.dataset.price || this.basePrice);
  }

  getItem() {
    const variant = ([...this.sizeOptions].find(option => option.checked)?.value) || this.variantId;

    return {
      id: variant,
      qty: 1,
      properties: {
        // 'Occasion': this.occasion,
        'Custom Delivery': this.dateField.value !== '' ? 'Delivery' : '',
        'Custom Date': this.dateField.value,
        'Gift Card Message': this.messageField.value,
        'Address': [this.addressField.value, this.zipField.value, this.cityField.value].filter(Boolean).join(', '),
        '_zip': this.zipField.value,
        // '_offer': 'the celebration',
      }
    };
  }

  reset(selected = false) {
    this.messageField.value = '';
    this.dateField.value = '';
    this.addressField.value = '';
    this.zipField.value = '';
    this.cityField.value = '';
    this.selected = selected;
    this.messageField.dispatchEvent(new Event('input'));
  }

  udpateSelected(selected) {
    this.selected = selected;

    this.addBtn.classList.toggle('hide', this.selected);
    this.removeBtn.classList.toggle('hide', !this.selected);
    this.addedLabel.classList.toggle('hide', !this.selected);
    this.customization.classList.toggle('hide', !this.selected);

    if (this.selected) {
      this.dateField.value = '';
      this.messageField.value = '';
    }

    document.dispatchEvent(new CustomEvent('celebration:updated'));
  }

  handleSizeChange(e) {
    document.dispatchEvent(new CustomEvent('celebration:updated'));
  }

  handleClone(e) {
    e.preventDefault();
    const clone = this.cloneNode(true);
    this.insertAdjacentElement('afterend', clone);
    clone.reset(true);

    document.dispatchEvent(new CustomEvent('celebration:updated'));
  }

  handleMassageSuggestion(e) {
    e.preventDefault();
    this.messageField.value = this.messageSuggesionOptions[this.messageSuggesionIdx];
    this.messageField.dispatchEvent(new Event('input'));
    this.messageSuggesionIdx = (this.messageSuggesionIdx + 1) % this.messageSuggesionOptions.length;
  }

  handleMessage(e) {
    const message = e.target.value;
    const count = message.length;
    this.messageCount.innerHTML = `${count}`;
  }

  handleAdd(e) {
    e.preventDefault();
    this.udpateSelected(true);
  }

  handleRemove(e) {
    e.preventDefault();
    this.udpateSelected(false);
  }
}

customElements.define('celebration-item', CelebrationItem);


// =======
// Gift Message Field
// =======
class GiftMessage extends HTMLElement {
  constructor() {
    super();

    // Get elements
    this.messageField = this.querySelector('[data-message-field]');
    this.messageCount = this.querySelector('[data-message-count]');

    // Bind methods
    this.messageField.addEventListener('input', this.handleMessage.bind(this));
  }

  handleMessage(e) {
    const message = e.target.value;
    const count = message.length;
    this.messageCount.innerHTML = `${count}`;
  }
}

customElements.define('gift-message', GiftMessage);


// =======
// Cross Sell
// =======
document.addEventListener('DOMContentLoaded', function() {
  const sizeChartDialog = document.getElementById('cross-sell-dialog');

  if (!sizeChartDialog) {
    return;
  }

  const closeBtn = sizeChartDialog.querySelector('.cross-sell__close');
  
  closeBtn.addEventListener('click', function() {
    sizeChartDialog.close();
  });

  // Close dialog when clicking on backdrop
  sizeChartDialog.addEventListener('click', function(e) {
    // Only close if the click was directly on the dialog backdrop
    // and not on any of its children
    if (e.target === sizeChartDialog) {
      sizeChartDialog.close();
    }
  });

  // Add loading state to Add to Cart buttons in cross-sell dialog
  const addToCartButtons = sizeChartDialog.querySelectorAll('.grid-product__atc .btn');
  
  addToCartButtons.forEach(button => {
    button.addEventListener('click', function() {
      this.classList.add('btn--loading');
    });
  });
});